import React, { createContext, useContext, ReactNode } from "react";
import { useScaleWithGestures } from "../components/TiptapEditor/useScaleWithGestures";
import { SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";
import { getReport, Report } from "../api/report";
import useTestId from "./useTestId";

interface ReportContextType {
    scale: number;
    setScale: (scale: number) => void;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

interface ReportProviderProps {
    children: ReactNode;
}

export const ReportProvider: React.FC<ReportProviderProps> = ({ children }) => {
    const [scale, setScale] = useScaleWithGestures();

    return (
        <ReportContext.Provider
            value={{
                scale,
                setScale,
            }}
        >
            {children}
        </ReportContext.Provider>
    );
};

export const useReport = (
    options?: { disableFetch?: boolean } & SWRConfiguration<Report | null>
) => {
    const context = useContext(ReportContext);
    if (context === undefined) {
        throw new Error(
            "useReportContext must be used within a ReportProvider"
        );
    }

    const testId = useTestId();
    const {
        data: value,
        isLoading: loading,
        error,
        mutate,
    } = useSWRImmutable<Report | null>(
        testId && !options?.disableFetch ? `/report/${testId}` : null,
        async () => {
            const response = await getReport({ testId: testId! });
            return response;
        },
        options
    );

    return {
        ...context,
        report: value || null,
        html: value?.html,
        logo: value?.logo_base64,
        status: value?.status,
        defaultFontSize: value?.font_size,
        mutate,
        loading,
        error,
    };
};
